import { useCallback, useMemo, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";

import { SORT_DIRECTION, toSortDirection } from "../utils/sortingToApi";

import type { RowData, SortingState } from "@tanstack/react-table";

import type {
  DataTableColumnDef,
  DataTableStateControls,
  DataTableStateValue,
  FilterOption,
  ParamUpdates,
  UseDataTableStateOptions,
} from "../components/dataTable/dataTable.types";

const DEFAULT_PAGE_SIZE = 10;
const MIN_PAGE_SIZE = 1;
const MAX_PAGE_SIZE = 100;
const DEFAULT_URL_SYNC = { prefix: "" };

function getColumnId<TData extends RowData>(
  column: DataTableColumnDef<TData>,
): string | undefined {
  if (column.id) return column.id;
  if ("accessorKey" in column && typeof column.accessorKey === "string") {
    return column.accessorKey;
  }
  return undefined;
}

function isSortableColumn<TData extends RowData>(
  column: DataTableColumnDef<TData>,
): boolean {
  return (
    column.enableSorting !== false &&
    ("accessorKey" in column || "accessorFn" in column)
  );
}

function parseInteger(value: string | null): number | null {
  if (value === null) return null;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function applyParamUpdates(search: string, updates: ParamUpdates): string {
  const params = new URLSearchParams(search);

  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === "") {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  });

  return params.toString();
}

export default function useDataTableState<TData extends RowData>({
  columns,
  defaultSorting,
  urlSync = DEFAULT_URL_SYNC,
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: UseDataTableStateOptions<TData>): DataTableStateControls {
  const history = useHistory();
  const location = useLocation();
  const [memorySearch, setMemorySearch] = useState("");

  const isUrlSynced = urlSync !== false;
  const prefix = urlSync === false ? "" : urlSync.prefix;
  const defaultSortingId = defaultSorting?.id;
  const defaultSortingDesc = defaultSorting?.desc;

  const paramKey = useCallback(
    (name: string): string => (prefix ? `${prefix}_${name}` : name),
    [prefix],
  );

  const { sortableColumnIdList, filterOptionsById } = useMemo(() => {
    const sortableIdList: string[] = [];
    const optionsById: Record<string, FilterOption[]> = {};

    columns.forEach((column) => {
      const columnId = getColumnId(column);
      if (!columnId) return;

      if (isSortableColumn(column)) sortableIdList.push(columnId);

      const filterOptionList = column.meta?.filterOptions;
      if (filterOptionList && filterOptionList.length > 0) {
        optionsById[columnId] = filterOptionList;
      }
    });

    return {
      sortableColumnIdList: sortableIdList,
      filterOptionsById: optionsById,
    };
  }, [columns]);

  const currentSearch = isUrlSynced ? location.search : memorySearch;

  const params = useMemo(
    () => new URLSearchParams(currentSearch),
    [currentSearch],
  );
  const sortColumn = params.get(paramKey("sortColumn"));
  const sortDirection = params.get(paramKey("sortDirection"));

  const currentSorting = useMemo((): SortingState => {
    if (sortColumn && sortableColumnIdList.includes(sortColumn)) {
      const isKnownDirection =
        sortDirection === SORT_DIRECTION.ASC ||
        sortDirection === SORT_DIRECTION.DESC;

      return [
        {
          id: sortColumn,
          desc: isKnownDirection
            ? sortDirection === SORT_DIRECTION.DESC
            : (defaultSortingDesc ?? false),
        },
      ];
    }

    return defaultSortingId
      ? [{ id: defaultSortingId, desc: defaultSortingDesc ?? false }]
      : [];
  }, [
    sortColumn,
    sortDirection,
    sortableColumnIdList,
    defaultSortingId,
    defaultSortingDesc,
  ]);

  const filterSearch = new URLSearchParams(
    Object.keys(filterOptionsById).flatMap((columnId) => {
      const value = params.get(paramKey(columnId));
      return value === null ? [] : [[columnId, value]];
    }),
  ).toString();

  const filters = useMemo(() => {
    const filterParams = new URLSearchParams(filterSearch);

    return Object.fromEntries(
      Object.entries(filterOptionsById).flatMap(
        ([columnId, filterOptionList]) => {
          const value = filterParams.get(columnId);

          return value !== null &&
            filterOptionList.some((option) => option.value === value)
            ? [[columnId, value]]
            : [];
        },
      ),
    );
  }, [filterSearch, filterOptionsById]);

  const page = parseInteger(params.get(paramKey("page"))) ?? 1;
  const perPage =
    parseInteger(params.get(paramKey("perPage"))) ?? defaultPageSize;
  const currentPageIndex = Math.max(1, page) - 1;
  const currentPageSize = Math.min(
    MAX_PAGE_SIZE,
    Math.max(MIN_PAGE_SIZE, perPage),
  );
  const currentSearchText = params.get(paramKey("search")) ?? "";

  const state = useMemo(
    (): DataTableStateValue => ({
      pageIndex: currentPageIndex,
      pageSize: currentPageSize,
      sorting: currentSorting,
      search: currentSearchText,
      filters,
    }),
    [
      currentPageIndex,
      currentPageSize,
      currentSorting,
      currentSearchText,
      filters,
    ],
  );

  const writeParams = useCallback(
    (updates: ParamUpdates): void => {
      const prefixedUpdates: ParamUpdates = Object.fromEntries(
        Object.entries(updates).map(([name, value]) => [paramKey(name), value]),
      );

      if (isUrlSynced) {
        history.replace({
          ...history.location,
          search: applyParamUpdates(history.location.search, prefixedUpdates),
        });
        return;
      }

      setMemorySearch((previous) =>
        applyParamUpdates(previous, prefixedUpdates),
      );
    },
    [history, isUrlSynced, paramKey],
  );

  const setPagination = useCallback(
    (pageIndex: number, pageSize: number): void =>
      writeParams({
        page: pageIndex > 0 ? String(pageIndex + 1) : null,
        perPage: String(pageSize),
      }),
    [writeParams],
  );

  const setSorting = useCallback(
    (sorting: SortingState): void => {
      const [firstSort] = sorting;

      writeParams({
        sortColumn: firstSort ? firstSort.id : null,
        sortDirection: firstSort ? toSortDirection(firstSort.desc) : null,
        page: null,
      });
    },
    [writeParams],
  );

  const setSearch = useCallback(
    (search: string): void =>
      writeParams({ search: search === "" ? null : search, page: null }),
    [writeParams],
  );

  const setFilter = useCallback(
    (columnId: string, value: string | null): void =>
      writeParams({ [columnId]: value, page: null }),
    [writeParams],
  );

  const resetFilters = useCallback((): void => {
    const clearedFilters: ParamUpdates = Object.fromEntries(
      Object.keys(filterOptionsById).map((columnId) => [columnId, null]),
    );

    writeParams({ ...clearedFilters, search: null, page: null });
  }, [filterOptionsById, writeParams]);

  return useMemo(
    () => ({
      state,
      setPagination,
      setSorting,
      setSearch,
      setFilter,
      resetFilters,
    }),
    [state, setPagination, setSorting, setSearch, setFilter, resetFilters],
  );
}
